import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, 
  Target,
  BarChart3,
  ChevronRight
} from "lucide-react";
import ProgressRing from "@/components/ProgressRing";
import TrialPill from "@/components/TrialPill";
import ActivityHeatmap from "@/components/ActivityHeatmap";

const ProgressPage = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressData, subjectsData] = await Promise.all([
          api.get("/progress"),
          api.get("/subjects")
        ]);
        setProgress(progressData.progress);
        setSubjects(subjectsData.subjects);
      } catch (error) {
        console.error("Failed to fetch progress:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading progress...</p>
        </div>
      </div>
    );
  }

  const subjectStats = progress?.subject_stats || [];
  const sortedByAccuracy = [...subjectStats].sort((a, b) => b.accuracy - a.accuracy);
  const strongSubjects = sortedByAccuracy.filter(s => s.accuracy >= 70 && s.answered >= 5);
  const weakSubjects = sortedByAccuracy.filter(s => s.accuracy < 50 && s.answered >= 3);

  return (
    <div data-testid="progress-page" className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl md:text-3xl font-bold">Progress Analytics</h1>
          <TrialPill />
        </div>
        <p className="text-muted-foreground">Track your improvement over time</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-6 border border-border">
          <p className="text-sm text-muted-foreground mb-2">Total Questions</p>
          <p className="text-3xl font-bold">{progress?.questions_answered || 0}</p>
        </div>
        <div className="glass rounded-2xl p-6 border border-border">
          <p className="text-sm text-muted-foreground mb-2">Correct Answers</p>
          <p className="text-3xl font-bold text-success">{progress?.correct_answers || 0}</p>
        </div>
        <div className="glass rounded-2xl p-6 border border-border">
          <p className="text-sm text-muted-foreground mb-2">Overall Accuracy</p>
          <p className="text-3xl font-bold">{progress?.accuracy || 0}%</p>
        </div>
        <div className="glass rounded-2xl p-6 border border-border">
          <p className="text-sm text-muted-foreground mb-2">Total XP</p>
          <p className="text-3xl font-bold text-primary">{progress?.total_xp?.toLocaleString() || 0}</p>
        </div>
      </div>

      {/* Subject Performance */}
      <div className="glass rounded-2xl p-6 border border-border">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Subject Performance</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/practice")}>
            Practice <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <div className="space-y-4">
          {subjectStats.map((stat) => {
            const subject = subjects.find(s => s.id === stat.id);
            const isStrong = stat.accuracy >= 70 && stat.answered >= 5;
            const isWeak = stat.accuracy < 50 && stat.answered >= 3;
            
            return (
              <div key={stat.id} className="group">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: subject?.color || '#6B7280' }}
                    />
                    <span className="font-medium">{stat.name}</span>
                    {stat.answered === 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                        Not started
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      {stat.correct}/{stat.answered}
                    </span>
                    <span className={`text-sm font-bold w-12 text-right ${
                      isStrong ? 'text-success' : 
                      isWeak ? 'text-destructive' : 
                      'text-muted-foreground'
                    }`}>
                      {stat.accuracy}%
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <Progress 
                    value={stat.accuracy} 
                    className={`h-2 ${
                      isStrong ? '[&>div]:bg-success' : 
                      isWeak ? '[&>div]:bg-destructive' : ''
                    }`}
                  />
                  <button
                    onClick={() => navigate(`/practice/${stat.id}`)}
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-end pr-2"
                  >
                    <span className="text-xs px-2 py-1 rounded-full bg-primary text-white">
                      Practice →
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Strengths & Weaknesses */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Strong Areas */}
        <div className="glass rounded-2xl p-6 border border-success/20 bg-success/5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-success" />
            <h2 className="text-lg font-semibold">Strong Areas</h2>
          </div>
          {strongSubjects.length > 0 ? (
            <div className="space-y-3">
              {strongSubjects.slice(0, 5).map((stat) => (
                <div key={stat.id} className="flex items-center justify-between">
                  <span>{stat.name}</span>
                  <span className="text-success font-bold">{stat.accuracy}%</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Complete more questions to identify your strengths
            </p>
          )}
        </div>

        {/* Weak Areas */}
        <div className="glass rounded-2xl p-6 border border-destructive/20 bg-destructive/5">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-destructive" />
            <h2 className="text-lg font-semibold">Focus Areas</h2>
          </div>
          {weakSubjects.length > 0 ? (
            <div className="space-y-3">
              {weakSubjects.slice(0, 5).map((stat) => (
                <div key={stat.id} className="flex items-center justify-between">
                  <span>{stat.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-destructive font-bold">{stat.accuracy}%</span>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => navigate(`/practice/${stat.id}`)}
                      className="h-6 px-2 text-xs"
                    >
                      Practice
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Great job! No weak areas detected yet
            </p>
          )}
        </div>
      </div>

      {/* Activity Heatmap */}
      <div className="glass rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Study Activity</h2>
        </div>
        <ActivityHeatmap />
      </div>

      {/* Mastery Progress */}
      <div className="glass rounded-2xl p-6 border border-border">
        <h2 className="text-lg font-semibold mb-6">Subject Mastery</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {subjectStats.slice(0, 10).map((stat) => {
            const subject = subjects.find(s => s.id === stat.id);
            return (
              <div key={stat.id} className="text-center">
                <div className="w-16 h-16 mx-auto mb-2">
                  <ProgressRing 
                    progress={stat.accuracy} 
                    size={64} 
                    strokeWidth={6}
                    color={subject?.color || 'hsl(var(--primary))'}
                  />
                </div>
                <p className="text-xs font-medium truncate">{stat.name}</p>
                <p className="text-xs text-muted-foreground">{stat.answered} Q</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressPage;
